export const timezones = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Adak',
  'America/Toronto',
  'America/Vancouver',
  'America/Mexico_City',
  'America/Monterrey',
  'America/Guatemala',
  'America/Managua',
  'America/San_Jose',
  'America/Panama',
  'America/Havana',
  'America/Santo_Domingo',
  'America/Caracas',
  'America/Bogota',
  'America/Lima',
  'America/La_Paz',
  'America/Santiago',
  'America/Asuncion',
  'America/Montevideo',
  'America/Buenos_Aires',
  'America/Sao_Paulo',
  'America/Fortaleza',
  'America/Recife',
  'America/Manaus',
  'America/Cayenne',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Europe/Madrid',
  'Europe/Paris',
  'Europe/Brussels',
  'Europe/Amsterdam',
  'Europe/Berlin',
  'Europe/Rome',
  'Europe/Vienna',
  'Europe/Prague',
  'Europe/Warsaw',
  'Europe/Budapest',
  'Europe/Bucharest',
  'Europe/Sofia',
  'Europe/Athens',
  'Europe/Helsinki',
  'Europe/Stockholm',
  'Europe/Oslo',
  'Europe/Copenhagen',
  'Europe/Riga',
  'Europe/Tallinn',
  'Europe/Vilnius',
  'Europe/Kiev',
  'Europe/Moscow',
  'Europe/Istanbul',
  'Asia/Dubai',
  'Asia/Kuwait',
  'Asia/Baghdad',
  'Asia/Riyadh',
  'Asia/Tehran',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Colombo',
  'Asia/Kathmandu',
  'Asia/Yangon',
  'Asia/Bangkok',
  'Asia/Ho_Chi_Minh',
  'Asia/Jakarta',
  'Asia/Singapore',
  'Asia/Kuala_Lumpur',
  'Asia/Manila',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Asia/Taipei',
  'Asia/Seoul',
  'Asia/Tokyo',
  'Asia/Ulaanbaatar',
  'Asia/Vladivostok',
  'Asia/Yakutsk',
  'Asia/Irkutsk',
  'Asia/Krasnoyarsk',
  'Asia/Novosibirsk',
  'Asia/Tashkent',
  'Asia/Almaty',
  'Asia/Baku',
  'Asia/Yerevan',
  'Asia/Tbilisi',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Africa/Nairobi',
  'Africa/Casablanca',
  'Africa/Addis_Ababa',
  'Africa/Accra',
  'Africa/Dar_es_Salaam',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Darwin',
  'Australia/Hobart',
  'Pacific/Auckland',
  'Pacific/Fiji',
  'Pacific/Honolulu',
  'Pacific/Guam',
  'Pacific/Port_Moresby',
  'Pacific/Apia',
  'Pacific/Tongatapu',
] as const

export const dateFormats = [
  { value: 'Y-m-d', label: 'YYYY-MM-DD (2025-11-07)' },
  { value: 'm/d/Y', label: 'MM/DD/YYYY (11/07/2025)' },
  { value: 'd/m/Y', label: 'DD/MM/YYYY (07/11/2025)' },
  { value: 'F j, Y', label: 'Month Day, Year (November 7, 2025)' },
] as const

export const timeFormats = [
  { value: 'H:i:s', label: '24-hour (14:30:00)' },
  { value: 'h:i:s A', label: '12-hour (02:30:00 PM)' },
] as const

const APP_TIMEZONE = 'Asia/Manila'

const NAIVE_DATETIME_RE =
  /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/

const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

type WallClockParts = {
  year: number
  month: number
  day: number
  hours: number
  minutes: number
}

export function parseWallClockParts(value: string): WallClockParts | null {
  const trimmed = value.trim()
  if (/[zZ]$/.test(trimmed) || /[+-]\d{2}:\d{2}$/.test(trimmed)) {
    return null
  }

  const match = NAIVE_DATETIME_RE.exec(trimmed)
  if (!match) return null

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const hours = Number(match[4])
  const minutes = Number(match[5])

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hours) ||
    !Number.isFinite(minutes)
  ) {
    return null
  }

  return { year, month, day, hours, minutes }
}

function formatWallClockParts(parts: WallClockParts): string {
  const month = MONTH_NAMES_SHORT[parts.month - 1] ?? String(parts.month)
  const hours = parts.hours
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours % 12 || 12
  const displayMinutes = String(parts.minutes).padStart(2, '0')
  return `${parts.day} ${month} ${parts.year} - ${displayHours}:${displayMinutes} ${ampm}`
}

function formatDateInAppTimezone(d: Date): string {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: APP_TIMEZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: false,
  })
  const parts = formatter.formatToParts(d)
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? '0')

  return formatWallClockParts({
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hours: get('hour'),
    minutes: get('minute'),
  })
}

/** Format appointment-style datetimes without unintended UTC shifts. */
export function formatWallClockDateTime(value?: string | null): string {
  if (!value) return '—'

  const parts = parseWallClockParts(value)
  if (parts) return formatWallClockParts(parts)

  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return value
    return formatDateInAppTimezone(d)
  } catch {
    return value
  }
}

/** Map API wall-clock datetime to datetime-local input value. */
export function toDatetimeLocalFromWallClock(value?: string | null): string {
  if (!value) return ''

  const parts = parseWallClockParts(value)
  if (parts) {
    const y = String(parts.year)
    const m = String(parts.month).padStart(2, '0')
    const day = String(parts.day).padStart(2, '0')
    const h = String(parts.hours).padStart(2, '0')
    const min = String(parts.minutes).padStart(2, '0')
    return `${y}-${m}-${day}T${h}:${min}`
  }

  const trimmed = value.trim()
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(trimmed)) return trimmed.slice(0, 16)
  const normalized = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T')
  const d = new Date(normalized)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const h = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${day}T${h}:${min}`
}

export function formatSlaDueText(
  slaDueAt: string | Date | null | undefined,
  opts?: { timezone?: string }
) {
  if (!slaDueAt) return null

  const d = typeof slaDueAt === 'string' ? new Date(slaDueAt) : slaDueAt
  if (Number.isNaN(d.getTime())) return null

  return d.toLocaleString('en-PH', {
    timeZone: opts?.timezone ?? APP_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}
